import { useCallback, useRef } from "react";

type TapEvent = React.MouseEvent | React.TouchEvent;

/**
 * Click and tap handlers for one control, without firing on a scroll.
 *
 * These controls live in a ProseMirror node view, where a tap does not
 * reliably produce a `click`, so they listen for `touchend` too. But
 * `touchend` is delivered to the element the touch *began* on, so a page
 * scroll that starts on a control would otherwise trigger it on lift — on a
 * thumbnail's remove button, that deletes the media.
 *
 * A touch that moved is a scroll, and the lift is ignored.
 */
export const useTapHandlers = (onTap: (event: TapEvent) => void) => {
  const moved = useRef(false);

  const onTouchStart = useCallback(() => {
    moved.current = false;
  }, []);

  const onTouchMove = useCallback(() => {
    moved.current = true;
  }, []);

  const onTouchEnd = useCallback(
    (event: React.TouchEvent) => {
      if (moved.current) return;

      // The synthesized click would otherwise run this a second time.
      event.preventDefault();
      event.stopPropagation();
      onTap(event);
    },
    [onTap],
  );

  return { onClick: onTap, onTouchStart, onTouchMove, onTouchEnd };
};
